<script setup>
import { computed, nextTick, ref } from 'vue'
import { useRoute } from 'vue-router'
import {
  PopoverContent,
  PopoverPortal,
  PopoverRoot,
  PopoverTrigger,
} from 'reka-ui'
import { groupHasActive, isNavItemActive } from '../navigation'

const props = defineProps({
  groups: { type: Array, default: () => [] },
  collapsed: { type: Boolean, default: false },
  badges: { type: Object, default: () => ({}) },
})

const emit = defineEmits(['navigate'])

const route = useRoute()
const openGroups = ref(readOpenGroups())
const railOpen = ref({})

function readOpenGroups() {
  try {
    const value = JSON.parse(localStorage.getItem('church_nav_open_groups') || '{}')
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {}
  } catch {
    return {}
  }
}

function persistOpenGroups(value) {
  try {
    localStorage.setItem('church_nav_open_groups', JSON.stringify(value))
  } catch {
    // Preferences just won't persist (private mode / full quota).
  }
}

function hasActive(group) {
  return groupHasActive(group, route.path)
}

/** Saved state per group; default open. The active group is always open. */
function isGroupOpen(group) {
  if (hasActive(group)) return true
  return openGroups.value[group.key] !== false
}

function toggleGroup(group) {
  if (hasActive(group)) return
  openGroups.value = { ...openGroups.value, [group.key]: !isGroupOpen(group) }
  persistOpenGroups(openGroups.value)
}

function badgeFor(item) {
  return props.badges?.[item.route] || 0
}

function badgeText(count) {
  return count > 99 ? '99+' : String(count)
}

function groupBadge(group) {
  return group.items.reduce((sum, item) => sum + badgeFor(item), 0)
}

function navigate(item) {
  railOpen.value = {}
  emit('navigate', item.route)
}

/** Open a rail flyout and move focus into it so the pages stay keyboard-reachable. */
function openRail(group) {
  railOpen.value[group.key] = true
  nextTick(() => focusFirstFlyoutItem(20))
}

// The flyout is portaled after a paint or two; retry briefly instead of guessing a delay.
function focusFirstFlyoutItem(retriesLeft) {
  const first = document.querySelector('[data-rail-flyout] button')
  if (first) {
    first.focus()
    return
  }
  if (retriesLeft > 0) setTimeout(() => focusFirstFlyoutItem(retriesLeft - 1), 50)
}

/* ---------- keyboard: roving rows inside one composite nav ---------- */
function navRows(container) {
  return [...container.querySelectorAll('[data-nav-row]')].filter(
    (row) => row.offsetParent !== null && !row.disabled && !row.closest('[inert]'),
  )
}

function onNavKeydown(event) {
  const container = event.currentTarget
  const rows = navRows(container)
  if (!rows.length) return
  const index = rows.indexOf(document.activeElement)
  const focusAt = (i) => rows[Math.max(0, Math.min(i, rows.length - 1))]?.focus()

  if (event.key === 'ArrowDown') {
    event.preventDefault()
    focusAt(index + 1)
  } else if (event.key === 'ArrowUp') {
    event.preventDefault()
    focusAt(index < 0 ? 0 : index - 1)
  } else if (event.key === 'Home') {
    event.preventDefault()
    focusAt(0)
  } else if (event.key === 'End') {
    event.preventDefault()
    focusAt(rows.length - 1)
  } else if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
    const groupKey = document.activeElement?.dataset?.group
    if (!groupKey) return
    const group = props.groups.find((g) => g.key === groupKey)
    if (!group || hasActive(group)) return
    event.preventDefault()
    if (props.collapsed) {
      if (event.key === 'ArrowRight') openRail(group)
      if (event.key === 'ArrowLeft') railOpen.value[group.key] = false
      return
    }
    if (event.key === 'ArrowRight' && !isGroupOpen(group)) toggleGroup(group)
    if (event.key === 'ArrowLeft' && isGroupOpen(group)) toggleGroup(group)
  }
}

function onNavFocus(event) {
  if (event.target !== event.currentTarget) return
  navRows(event.currentTarget)[0]?.focus()
}
</script>

<template>
  <!-- Icon rail: one row per group; each opens a flyout with its pages. -->
  <nav
    v-if="collapsed"
    class="space-y-1"
    aria-label="Main"
    tabindex="0"
    @keydown="onNavKeydown"
    @focus="onNavFocus"
  >
    <PopoverRoot v-for="group in groups" :key="group.key" v-model:open="railOpen[group.key]">
      <PopoverTrigger as-child>
        <button
          type="button"
          class="nav-row justify-center !px-0"
          :class="hasActive(group) ? 'nav-row-active' : ''"
          :aria-label="group.label"
          :aria-expanded="!!railOpen[group.key]"
          :title="group.label"
          :data-group="group.key"
          data-nav-row
          tabindex="-1"
          @click="openRail(group)"
        >
          <span :class="[group.icon, 'nav-icon']" aria-hidden="true" />
          <span v-if="groupBadge(group)" class="nav-dot nav-dot-rail nav-dot-badge" aria-hidden="true" />
          <span v-else-if="hasActive(group)" class="nav-dot nav-dot-rail nav-dot-active" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverPortal>
        <PopoverContent
          side="right"
          align="start"
          :side-offset="10"
          class="z-[130] w-56 rounded-lg border border-outline-gray-1 bg-surface-elevation-2 p-1 shadow-xl outline-none"
          @keydown.escape.prevent="railOpen[group.key] = false"
        >
          <div data-rail-flyout>
            <div class="section-label">{{ group.label }}</div>
            <button
              v-for="item in group.items"
              :key="item.route"
              type="button"
              class="nav-row"
              :class="isNavItemActive(item, route.path) ? 'nav-row-active' : ''"
              :aria-current="isNavItemActive(item, route.path) ? 'page' : undefined"
              @click="navigate(item)"
            >
              <span :class="[item.icon, 'nav-icon']" aria-hidden="true" />
              <span class="nav-label">{{ item.label }}</span>
              <span v-if="badgeFor(item)" class="nav-badge">{{ badgeText(badgeFor(item)) }}</span>
            </button>
          </div>
        </PopoverContent>
      </PopoverPortal>
    </PopoverRoot>
  </nav>

  <!-- Expanded: one continuous list of labelled, collapsible groups. -->
  <nav
    v-else
    class="space-y-3"
    aria-label="Main"
    tabindex="0"
    @keydown="onNavKeydown"
    @focus="onNavFocus"
  >
    <section v-for="group in groups" :key="group.key" class="min-w-0">
      <button
        type="button"
        class="nav-group-toggle"
        :aria-expanded="isGroupOpen(group)"
        :aria-controls="`nav-group-${group.key}`"
        :data-group="group.key"
        data-nav-row
        tabindex="-1"
        @click="toggleGroup(group)"
      >
        <span class="nav-group-label">{{ group.label }}</span>
        <span v-if="groupBadge(group)" class="nav-badge">{{ badgeText(groupBadge(group)) }}</span>
        <span
          v-else-if="hasActive(group) && !isGroupOpen(group)"
          class="nav-dot nav-dot-active"
          aria-hidden="true"
        />
        <span
          class="lucide-chevron-down nav-chevron"
          :class="isGroupOpen(group) ? '' : '-rotate-90'"
          aria-hidden="true"
        />
      </button>

      <div
        :id="`nav-group-${group.key}`"
        class="nav-children"
        :inert="!isGroupOpen(group)"
        :style="{ gridTemplateRows: isGroupOpen(group) ? '1fr' : '0fr' }"
      >
        <div class="nav-children-inner">
          <div class="space-y-0.5 pb-0.5 pt-1">
            <button
              v-for="item in group.items"
              :key="item.route"
              type="button"
              class="nav-row"
              :class="isNavItemActive(item, route.path) ? 'nav-row-active' : ''"
              :aria-current="isNavItemActive(item, route.path) ? 'page' : undefined"
              data-nav-row
              tabindex="-1"
              @click="navigate(item)"
            >
              <span :class="[item.icon, 'nav-icon']" aria-hidden="true" />
              <span class="nav-label">{{ item.label }}</span>
              <span v-if="badgeFor(item)" class="nav-badge">{{ badgeText(badgeFor(item)) }}</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  </nav>
</template>

<style scoped>
.nav-row {
  @apply flex w-full items-center gap-2 px-2 text-sm text-ink-gray-7 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-outline-blue-4;
  height: var(--nav-row-h, 2.25rem);
  border-radius: var(--nav-row-radius, 6px);
}
.nav-row:hover {
  @apply bg-surface-gray-1 text-ink-gray-9;
}
/* One active treatment everywhere: subtle fill + accent text/icon. */
.nav-row-active,
.nav-row-active:hover {
  @apply bg-surface-blue-2 font-medium text-ink-blue-9;
}
.nav-row-active .nav-icon {
  @apply text-ink-blue-9;
}
.nav-icon {
  @apply size-4 shrink-0 text-ink-gray-5;
}
.nav-label {
  @apply min-w-0 flex-1 truncate text-left;
}
.nav-badge {
  @apply shrink-0 rounded-full bg-surface-red-2 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-ink-red-7;
}
.nav-dot {
  @apply size-1.5 shrink-0 rounded-full;
}
.nav-dot-active {
  background: var(--ink-blue-9, #1d4ed8);
}
.nav-dot-badge {
  background: var(--ink-red-5, #ef4444);
}
.nav-dot-rail {
  position: absolute;
  right: 0.45rem;
  top: 0.45rem;
}
.nav-group-toggle {
  @apply flex w-full items-center gap-2 rounded px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-ink-gray-5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-outline-blue-4;
}
.nav-group-toggle:hover {
  @apply text-ink-gray-7;
}
.nav-group-label {
  @apply min-w-0 flex-1 truncate text-left;
}
.nav-chevron {
  @apply size-3 shrink-0 transition-transform;
}
.nav-children {
  display: grid;
  transition: grid-template-rows 200ms ease;
}
.nav-children-inner {
  overflow: hidden;
}
@media (prefers-reduced-motion: reduce) {
  .nav-children,
  .nav-chevron {
    transition: none;
  }
}
.section-label {
  @apply px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-ink-gray-5;
}
</style>
